import { useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Search, FileText, CreditCard, Copy, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminOutstanding = ({ adminToken }) => {
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  const searchOutstanding = async () => {
    if (!phone.trim()) { toast.error('Enter a phone number'); return; }
    setLoading(true);
    setResult(null);
    setPaymentLink(null);
    setSelectedIds([]);
    try {
      const res = await axios.get(`${API}/admin/customers/outstanding?phone=${phone.trim()}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setResult(res.data);
      setSelectedIds(res.data.orders.map(o => o.id));
    } catch (err) {
      toast.error('Failed to fetch outstanding orders');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectedOrders = result?.orders?.filter(o => selectedIds.includes(o.id)) || [];
  const selectedTotal = selectedOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  const generatePDF = () => {
    if (!selectedOrders.length) { toast.error('Select at least one order'); return; }

    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(217, 119, 6);
    doc.text('HHouse of Breadss', 20, 22);
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('OUTSTANDING BALANCE STATEMENT', 20, 32);

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, 40);

    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(0.8);
    doc.line(20, 45, 190, 45);

    // Customer details
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(`Customer: ${result.customer_name || 'N/A'}`, 20, 55);
    doc.text(`Phone: ${result.phone}`, 20, 62);

    // Table of outstanding orders
    const tableRows = selectedOrders.map(order => {
      const invNo = `INV-${order.id.slice(0, 8).toUpperCase()}`;
      const itemsText = (order.items || [])
        .map(i => `${i.product?.name || i.name} ×${i.quantity}`)
        .join(', ');
      const delivDate = order.scheduled_date || order.created_at?.slice(0, 10) || 'N/A';
      const delivType = order.delivery_type === 'pickup' ? 'Pickup' : 'Delivery';
      return [invNo, itemsText, delivType, delivDate, `Rs ${(order.total || 0).toFixed(0)}`];
    });

    autoTable(doc, {
      startY: 70,
      head: [['Invoice #', 'Items Ordered', 'Type', 'Date', 'Amount']],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [217, 119, 6], textColor: 255, fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 80 },
        2: { cellWidth: 20 },
        3: { cellWidth: 28 },
        4: { cellWidth: 24, halign: 'right' }
      },
      alternateRowStyles: { fillColor: [255, 248, 240] }
    });

    const finalY = doc.lastAutoTable.finalY + 8;

    // Total outstanding box
    doc.setFillColor(254, 243, 199);
    doc.roundedRect(120, finalY, 70, 20, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text('TOTAL OUTSTANDING:', 125, finalY + 8);
    doc.setFontSize(14);
    doc.setTextColor(217, 119, 6);
    doc.setFont(undefined, 'bold');
    doc.text(`Rs. ${selectedTotal.toFixed(0)}`, 185, finalY + 16, { align: 'right' });
    doc.setFont(undefined, 'normal');

    if (paymentLink) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text('Payment Link:', 20, finalY + 12);
      doc.setTextColor(217, 119, 6);
      const lines = doc.splitTextToSize(paymentLink, 90);
      doc.text(lines, 20, finalY + 18);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(120);
    const fy = doc.internal.pageSize.height - 15;
    doc.line(20, fy - 3, 190, fy - 3);
    doc.text('HHouse of Breadss · R3/015A, M3M Cosmopolitan, Sec 66, Gurugram · +91-9999183418', 105, fy, { align: 'center' });

    doc.save(`Outstanding_${result.phone}_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('Outstanding statement downloaded!');
  };

  const createPaymentLink = async () => {
    if (!selectedOrders.length) { toast.error('Select at least one order'); return; }
    setGeneratingLink(true);
    try {
      const res = await axios.post(`${API}/admin/customers/outstanding-payment-link`, {
        phone: result.phone,
        name: result.customer_name,
        order_ids: selectedIds
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setPaymentLink(res.data.payment_link);
      toast.success(`Payment link created for ₹${res.data.total}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-accent">Outstanding Balance</h2>
        <p className="text-sm text-muted-foreground">Find all pending payments for a customer</p>
      </div>

      {/* Search */}
      <Card className="p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Customer phone number..."
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchOutstanding()}
              className="pl-10 h-10"
              data-testid="outstanding-phone-input"
            />
          </div>
          <Button onClick={searchOutstanding} disabled={loading} className="h-10 gap-2" data-testid="outstanding-search-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Search
          </Button>
        </div>
      </Card>

      {/* Results */}
      {result && (
        <>
          {result.orders.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
              <h3 className="font-semibold text-accent">No Outstanding Balance</h3>
              <p className="text-sm text-muted-foreground">All orders from {result.phone} have been paid</p>
            </Card>
          ) : (
            <>
              {/* Summary bar */}
              <Card className="p-4 bg-amber-50 border-amber-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="font-semibold text-accent text-lg">{result.customer_name || result.phone}</p>
                    <p className="text-sm text-muted-foreground">{result.orders.length} unpaid order{result.orders.length > 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Selected Total</p>
                    <p className="text-2xl font-bold text-primary">₹{selectedTotal.toFixed(0)}</p>
                  </div>
                </div>
              </Card>

              {/* Order list */}
              <div className="space-y-2" data-testid="outstanding-orders-list">
                {result.orders.map(order => {
                  const isSelected = selectedIds.includes(order.id);
                  const itemsText = (order.items || []).map(i => `${i.product?.name || i.name} ×${i.quantity}`).join(' · ');
                  return (
                    <Card
                      key={order.id}
                      className={`p-3 cursor-pointer transition-all border-2 ${isSelected ? 'border-primary bg-amber-50' : 'border-transparent'}`}
                      onClick={() => toggleOrder(order.id)}
                      data-testid={`outstanding-order-${order.id.slice(0, 8)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${isSelected ? 'bg-primary border-primary' : 'border-gray-300'}`}>
                          {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-mono text-xs font-bold text-accent">INV-{order.id.slice(0, 8).toUpperCase()}</span>
                            <span className="font-bold text-primary text-sm shrink-0">₹{(order.total || 0).toFixed(0)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{itemsText}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>{order.scheduled_date || order.created_at?.slice(0, 10)}</span>
                            <Badge variant="outline" className="text-[10px] py-0">{order.delivery_type}</Badge>
                            <Badge variant="outline" className="text-[10px] py-0 text-orange-600 border-orange-300">{order.payment_status}</Badge>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Select all */}
              <div className="flex items-center gap-2 text-sm">
                <button onClick={() => setSelectedIds(result.orders.map(o => o.id))} className="text-primary underline">Select all</button>
                <span className="text-muted-foreground">·</span>
                <button onClick={() => setSelectedIds([])} className="text-muted-foreground underline">Deselect all</button>
                <span className="text-muted-foreground ml-2">{selectedIds.length} of {result.orders.length} selected</span>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" className="flex-1 gap-2" onClick={generatePDF} disabled={!selectedIds.length}>
                  <FileText className="h-4 w-4" />
                  Download PDF Statement
                </Button>
                <Button
                  className="flex-1 gap-2 bg-primary"
                  onClick={createPaymentLink}
                  disabled={!selectedIds.length || generatingLink}
                  data-testid="create-outstanding-payment-btn"
                >
                  {generatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  Create Payment Link · ₹{selectedTotal.toFixed(0)}
                </Button>
              </div>

              {/* Generated payment link */}
              {paymentLink && (
                <Card className="p-4 bg-green-50 border-green-200">
                  <p className="text-sm font-semibold text-green-800 mb-2">Payment link created!</p>
                  <div className="flex items-center gap-2">
                    <Input value={paymentLink} readOnly className="text-xs h-9 font-mono" />
                    <Button size="sm" variant="outline" className="shrink-0 h-9" onClick={() => { navigator.clipboard.writeText(paymentLink); toast.success('Copied!'); }}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="shrink-0 h-9" onClick={() => window.open(paymentLink, '_blank')}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-green-700 mt-2">Send this link to {result.customer_name || result.phone} to collect ₹{selectedTotal.toFixed(0)}</p>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AdminOutstanding;
